import { useState, useCallback, useRef, useEffect } from "react";
import { v4 as uuid } from "uuid";
import { isEmpty } from "@util/utils";
import useMessageDialog from "@util/hook/useMessageDialog";
import useInvoiceApi from "@apis/useInvoiceApi";

/**
 * 管理發票常用資料的 Hook
 * 整合了表單資料管理（useFormDataWithKey）和套用常用資料功能
 *
 * @param {number} memberID - 會員 ID
 * @param {Object} [initialSource] - 初始資料來源（可選，用於從 cycleOrder 初始化）
 * @returns {Object} 包含 formData 和 applyDefaultInvoice 函數
 */
const useMemberDefaultInvoice = (memberID, initialSource = {}) => {
	const setMessageDialog = useMessageDialog();
	const { getMemberInvoiceApi } = useInvoiceApi();

	// 表單資料管理（整合 useFormDataWithKey）
	const [formData, setFormData] = useState({
		source: initialSource,
		key: uuid(),
	});

	// 快取已請求的資料
	const cachedDataRef = useRef(null);
	// 標記是否正在請求中
	const isLoadingRef = useRef(false);

	// 轉換發票資料格式
	const transformInvoiceData = useCallback(
		data => {
			if (isEmpty(data) || !data?.issuingType) {
				return null;
			}

			return {
				issuingType: data?.issuingType ?? 0,
				invoiceCarrierType: data?.invoiceCarrierType ?? data?.carrierType ?? 0,
				carrierNo: data?.invoiceCarrierNo ?? data.carrierNo ?? "",
				taxID: data?.taxID ?? "",
				taxTitle: data?.taxTitle ?? "",
				donationCode: data?.donationCode ?? "",
				issuingEmail: data?.issuingEmail ?? data?.mail ?? "",
				issuingPhone: data?.cellPhone ?? "",
			};
		},
		// eslint-disable-next-line
		[],
	);

	// 當 initialSource 變化時，更新 formData（用於從 cycleOrder 初始化）
	useEffect(
		() => {
			if (!isEmpty(initialSource)) {
				// 轉換初始資料格式
				const transformed = transformInvoiceData(initialSource);
				if (transformed) {
					setFormData({
						source: transformed,
						key: uuid(),
					});
				} else {
					// 如果轉換失敗，仍然設置原始資料（可能來自 cycleOrder，格式不同）
					const { mail, cellPhone, ...rest } = initialSource;

					setFormData({
						source: {
							...rest,
							issuingEmail: mail || "",
							issuingPhone: cellPhone || "",
						},
						key: uuid(),
					});
				}
			}
		},
		// eslint-disable-next-line
		[initialSource],
	);

	/**
	 * 套用常用發票
	 */
	const applyDefaultInvoice = useCallback(
		async () => {
			// 檢查 memberID 是否存在且有效
			if (!memberID || memberID <= 0) {
				setMessageDialog({
					open: true,
					title: "提醒",
					msg: "請先選擇會員，謝謝。",
				});
				return;
			}

			// 如果正在請求中，不重複請求
			if (isLoadingRef.current) {
				return;
			}

			// 如果已經有快取的資料，直接套用
			if (cachedDataRef.current !== null) {
				const transformedData = transformInvoiceData(cachedDataRef.current);

				if (isEmpty(transformedData)) {
					setMessageDialog({
						open: true,
						title: "提醒",
						msg: "很抱歉！無法取得相關資料，謝謝。",
					});
					return;
				}

				// 更新表單資料
				setFormData({
					source: transformedData,
					key: uuid(),
				});
				return;
			}

			// 開始請求
			isLoadingRef.current = true;

			try {
				const resp = await getMemberInvoiceApi({ memberID });

				if (isEmpty(resp)) {
					setMessageDialog({
						open: true,
						title: "提醒",
						msg: "很抱歉！無法取得相關資料，謝謝。",
					});
					isLoadingRef.current = false;
					return;
				}

				const invoiceData = resp;

				// 快取原始資料
				cachedDataRef.current = invoiceData;

				// 轉換資料格式
				const transformedData = transformInvoiceData(invoiceData);

				// 更新表單資料
				setFormData({
					source: transformedData,
					key: uuid(),
				});
			} catch (error) {
				setMessageDialog({
					open: true,
					title: "提醒",
					msg: "很抱歉！無法取得相關資料，謝謝。",
				});
			} finally {
				isLoadingRef.current = false;
			}
		},
		// eslint-disable-next-line
		[memberID, getMemberInvoiceApi, transformInvoiceData, setMessageDialog],
	);

	useEffect(() => {
		if (cachedDataRef.current) {
			// 重置快取資料
			cachedDataRef.current = null;

			// 重置表單資料
			setFormData({
				source: {},
				key: uuid(),
			});
		}
	}, [memberID]);

	return {
		formData, // { source, key }
		applyDefaultInvoice,
	};
};

export default useMemberDefaultInvoice;
